/**
 * InsuranceVerificationModal v2 — Real verification via document upload
 *
 * Two-panel layout following DocumentValidationModal pattern:
 * - Left: État de parc / Conditions particulières document viewer + upload
 * - Right: Guarantee entry (Phase A) or comparison table (Phase B)
 *
 * Flow:
 * 1. User uploads état de parc or conditions particulières PDF
 * 2. User enters actual guarantee data from the document (Phase A)
 * 3. System compares declared vs model expected (Phase B)
 * 4. User validates/overrides discrepancies
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrgCurrency } from '../../../hooks/useOrgCurrency.js';
import {
  X, Shield, ShieldAlert, ShieldCheck, ShieldQuestion, AlertTriangle, Check, Info,
  Loader2, Mail, FileWarning, Upload, FileText, Plus, Trash2, Save, ArrowRight
} from 'lucide-react';
import { insuranceModuleAPI } from '../../../api/insurance.js';
import { documentAPI } from '../../../api/documents.js';
import { API_BASE_URL, getAuthHeaders } from '../../../api/core.js';
import DocumentDropZone from '../../common/DocumentDropZone.jsx';

// Les libelles de severite sont resolus par t('insurance.verification.severity.<code>').
const SEVERITY_STYLES = {
  critical: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: AlertTriangle, severity: 'critical' },
  high: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: ShieldAlert, severity: 'high' },
  medium: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', icon: ShieldQuestion, severity: 'medium' },
  ok: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', icon: ShieldCheck, severity: 'ok' },
  info: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: Info, severity: 'info' },
};

// Nom d'une garantie : la traduction du code prime, sinon le libelle envoye par
// le backend, sinon le code brut.
function guaranteeLabel(t, code, fallbackLabel) {
  return t(`insurance.guaranteeCodes.${code}`, { defaultValue: fallbackLabel || code });
}

function InsuranceVerificationModal({ isOpen, onClose, vehicleId, vehicleInsuranceId, contractId, onVerified }) {
  const { t } = useTranslation();
  // Devise : celle du perimetre courant (une verification porte sur un seul contrat,
  // donc une seule filiale). Un seul outil monetaire, jamais d'EUR code en dur.
  const { moneyScope } = useOrgCurrency();
  const formatAmount = (amount) => (amount ? moneyScope(amount, { decimals: 0 }) : '');

  // Loading states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingDeclared, setSavingDeclared] = useState(false);
  const [error, setError] = useState(null);

  // Data states
  const [expectedData, setExpectedData] = useState(null); // from getExpectedGuarantees
  const [comparison, setComparison] = useState(null);      // from computeVerification
  const [overrides, setOverrides] = useState({});
  const [globalNotes, setGlobalNotes] = useState('');

  // Phase: 'entry' (Phase A) or 'comparison' (Phase B)
  const [phase, setPhase] = useState('entry');

  // Declared guarantee rows (editable form)
  const [declaredRows, setDeclaredRows] = useState([]);

  // Document states (left panel)
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // EML download
  const [emlDownloading, setEmlDownloading] = useState(false);

  // ── Load data on open ──
  useEffect(() => {
    if (!isOpen || !vehicleInsuranceId) return;
    loadInitialData();
    loadDocuments();
    return () => {
      if (blobUrl) window.URL.revokeObjectURL(blobUrl);
    };
  }, [isOpen, vehicleInsuranceId]);

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);
    setComparison(null);
    setPhase('entry');
    try {
      const res = await insuranceModuleAPI.getExpectedGuarantees(vehicleInsuranceId);
      if (!res.ok) {
        setError(res.message || res.error || t('insurance.verification.genericError'));
        return;
      }
      const data = res.data;
      setExpectedData(data);

      if (data.has_declared_data && data.declared) {
        // Already has declared data — initialize rows from it and try to compute
        setDeclaredRows(data.declared.map((d, i) => ({ ...d, _key: i })));
        await loadComparison();
      } else {
        // No declared data — initialize rows from expected (pre-fill from model)
        const rows = data.expected.map((g, i) => ({
          _key: i,
          code: g.code,
          label: g.label,
          included: true,
          limit: g.limit || '',
          deductible: g.deductible || '',
        }));
        setDeclaredRows(rows);
        setPhase('entry');
      }
    } catch (err) {
      setError(err.message || t('insurance.verification.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const loadComparison = async () => {
    try {
      const res = await insuranceModuleAPI.computeVerification(vehicleInsuranceId);
      if (res.ok && res.data?.has_declared_data) {
        setComparison(res.data);
        setPhase('comparison');
        // Auto-check conforming rows
        const autoOverrides = {};
        for (const row of res.data.comparison) {
          if (row.status === 'ok') autoOverrides[row.code] = true;
        }
        setOverrides(autoOverrides);
      } else if (res.data?.error === 'no_declared_data') {
        setPhase('entry');
      }
    } catch {
      // stay in entry phase
    }
  };

  // ── Documents (left panel) ──
  const loadDocuments = async () => {
    if (!contractId) return;
    try {
      const res = await documentAPI.getAll({
        scope: 'CONTRACT',
        reference_id: String(contractId),
      });
      const docs = res.data || res || [];
      const filtered = Array.isArray(docs)
        ? docs.filter(d =>
            d.document_type === 'FLEET_SCHEDULE' ||
            d.document_type === 'SPECIAL_CONDITIONS' ||
            d.document_type === 'SPECIAL_CONDITIONS' ||
            d.document_type === 'INSURANCE_POLICY'
          )
        : [];
      setDocuments(filtered);
      if (filtered.length > 0 && !selectedDoc) {
        selectDocument(filtered[0]);
      }
    } catch {
      // no docs
    }
  };

  const selectDocument = async (doc) => {
    setSelectedDoc(doc);
    if (blobUrl) window.URL.revokeObjectURL(blobUrl);
    setBlobUrl(null);
    if (!doc?.id) return;
    setLoadingPreview(true);
    try {
      const url = await documentAPI.getBlobUrl(doc.id, doc.mime_type);
      setBlobUrl(url);
    } catch {
      setBlobUrl(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDocumentUploaded = (uploadedDocs) => {
    loadDocuments();
  };

  // ── Declared guarantee rows editing ──
  const updateRow = (key, field, value) => {
    setDeclaredRows(prev => prev.map(r =>
      r._key === key ? { ...r, [field]: value } : r
    ));
  };

  const addRow = () => {
    setDeclaredRows(prev => [
      ...prev,
      { _key: Date.now(), code: '', label: '', included: true, limit: '', deductible: '' },
    ]);
  };

  const removeRow = (key) => {
    setDeclaredRows(prev => prev.filter(r => r._key !== key));
  };

  // ── Save declared data (Phase A → Phase B) ──
  const handleSaveDeclared = async () => {
    setSavingDeclared(true);
    setError(null);
    try {
      const guarantees = declaredRows.map(r => ({
        code: r.code,
        label: r.label,
        included: !!r.included,
        limit: r.limit ? parseFloat(r.limit) : null,
        deductible: r.deductible ? parseFloat(r.deductible) : null,
      }));

      const res = await insuranceModuleAPI.declareGuarantees(vehicleInsuranceId, guarantees);
      if (!res.ok) {
        setError(res.message || res.error || t('insurance.verification.saveError'));
        return;
      }

      // Now compute comparison
      await loadComparison();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingDeclared(false);
    }
  };

  // ── Validate verification (Phase B → done) ──
  const handleValidate = async () => {
    setSaving(true);
    try {
      const res = await insuranceModuleAPI.saveVerification(vehicleInsuranceId, {
        overrides,
        notes: globalNotes || undefined,
      });
      if (res.ok) {
        onVerified?.(res.data);
        onClose();
      } else {
        setError(res.message || res.error || t('insurance.verification.genericError'));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadEtatDeParc = async () => {
    if (!contractId && !comparison?.contract_id) return;
    setEmlDownloading(true);
    try {
      const cId = contractId || comparison?.contract_id;
      const url = `${API_BASE_URL}/email_templates/download-eml?entity_type=insurance_contract&entity_id=${cId}&template_code=INS_FLEET_SCHEDULE_REQUEST`;
      const headers = getAuthHeaders();
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error('EML download failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `demande_etat_de_parc_${cId}_${Date.now()}.eml`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      console.error('EML download error:', err);
    } finally {
      setEmlDownloading(false);
    }
  };

  const toggleOverride = (code) => {
    setOverrides(prev => ({ ...prev, [code]: !prev[code] }));
  };

  const switchToEntry = () => {
    setPhase('entry');
    setComparison(null);
  };

  if (!isOpen) return null;

  const conformCount = comparison ? comparison.comparison.filter(c => c.status === 'ok' || overrides[c.code]).length : 0;
  const totalCount = comparison ? comparison.comparison.length : 0;
  const unacceptedDiscrepancies = comparison ? comparison.discrepancies.filter(d => !overrides[d.code]).length : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{t('insurance.vehicleTab.verificationTitle')}</h2>
              {expectedData && (
                <p className="text-sm text-gray-500">
                  {t('insurance.verification.modelLabel')} <span className="font-medium">{expectedData.model_name}</span>
                  {phase === 'entry' && <span className="ml-2 text-amber-600 font-medium">{t('insurance.verification.phaseEntry')}</span>}
                  {phase === 'comparison' && <span className="ml-2 text-blue-600 font-medium">{t('insurance.verification.phaseComparison')}</span>}
                </p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body — Two panels */}
        <div className="flex-1 flex overflow-hidden">
          {/* LEFT PANEL: Document viewer */}
          <div className="w-[420px] flex-shrink-0 border-r border-gray-200 flex flex-col bg-gray-50">
            <div className="px-4 py-3 border-b border-gray-200 bg-white">
              <h3 className="text-sm font-semibold text-gray-700">{t('insurance.verification.docPanelTitle')}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{t('insurance.verification.docPanelSubtitle')}</p>
            </div>

            {/* Document tabs if multiple */}
            {documents.length > 1 && (
              <div className="flex gap-1 px-3 py-2 border-b border-gray-200 bg-white overflow-x-auto">
                {documents.map(doc => (
                  <button
                    key={doc.id}
                    onClick={() => selectDocument(doc)}
                    className={`px-2 py-1 text-xs rounded whitespace-nowrap ${
                      selectedDoc?.id === doc.id
                        ? 'bg-blue-100 text-blue-700 font-medium'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {doc.document_type === 'FLEET_SCHEDULE' ? t('insurance.documentTypes.fleetSchedule') :
                     doc.document_type === 'SPECIAL_CONDITIONS' ? t('insurance.verification.specialConditionsShort') :
                     doc.original_filename || t('insurance.verification.document')}
                  </button>
                ))}
              </div>
            )}

            {/* Document preview area */}
            <div className="flex-1 overflow-auto">
              {documents.length === 0 ? (
                <div className="p-4">
                  {/* Upload dropzone */}
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <FileWarning className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-semibold text-amber-800">
                          {t('insurance.verification.uploadPrompt')}
                        </p>
                        <p className="text-xs text-amber-700 mt-1">
                          {t('insurance.verification.uploadHint')}
                        </p>
                      </div>
                    </div>
                  </div>
                  {contractId && (
                    <DocumentDropZone
                      scope="CONTRACT"
                      referenceId={String(contractId)}
                      allowedTypes={['FLEET_SCHEDULE', 'SPECIAL_CONDITIONS']}
                      onUploadComplete={handleDocumentUploaded}
                      compact={true}
                    />
                  )}
                  <button
                    onClick={handleDownloadEtatDeParc}
                    disabled={emlDownloading}
                    className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-amber-800 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded-md transition-colors"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    {emlDownloading ? t('insurance.verification.generating') : t('insurance.verification.requestFleetSchedule')}
                  </button>
                </div>
              ) : loadingPreview ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                </div>
              ) : blobUrl ? (
                selectedDoc?.mime_type?.startsWith('image/') ? (
                  <img src={blobUrl} alt={t('insurance.verification.document')} className="w-full" />
                ) : selectedDoc?.mime_type === 'application/pdf' ? (
                  <iframe
                    src={`${blobUrl}#toolbar=0&navpanes=0`}
                    className="w-full h-full"
                    style={{ minHeight: '500px' }}
                    title={t('insurance.documentTypes.fleetSchedule')}
                  />
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">{t('insurance.verification.previewUnavailable')}</p>
                  </div>
                )
              ) : (
                <div className="p-8 text-center text-gray-400">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">{t('insurance.verification.selectDocument')}</p>
                </div>
              )}
            </div>

            {/* Upload more docs */}
            {documents.length > 0 && contractId && (
              <div className="px-4 py-2 border-t border-gray-200 bg-white">
                <DocumentDropZone
                  scope="CONTRACT"
                  referenceId={String(contractId)}
                  allowedTypes={['FLEET_SCHEDULE', 'SPECIAL_CONDITIONS']}
                  onUploadComplete={handleDocumentUploaded}
                  compact={true}
                />
              </div>
            )}
          </div>

          {/* RIGHT PANEL: Guarantee entry or comparison */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                <span className="ml-3 text-gray-500">{t('common.states.loading')}</span>
              </div>
            ) : error && !expectedData ? (
              <div className="m-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                <p className="font-medium">{t('insurance.messages.errorTitle')}</p>
                <p className="text-sm mt-1">{error}</p>
              </div>
            ) : phase === 'entry' ? (
              /* ── PHASE A: Data Entry ── */
              <div className="flex-1 overflow-y-auto px-6 py-4">
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>{t('insurance.verification.step1')}</strong> {t('insurance.verification.step1Body')}
                  </p>
                </div>

                {error && (
                  <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                    {error}
                  </div>
                )}

                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left">
                        <th className="px-3 py-2 font-semibold text-gray-700 w-10"></th>
                        <th className="px-3 py-2 font-semibold text-gray-700">{t('insurance.verification.col.guarantee')}</th>
                        <th className="px-3 py-2 font-semibold text-gray-700 text-center w-32">{t('insurance.verification.col.expected')}</th>
                        <th className="px-3 py-2 font-semibold text-gray-700 text-center w-20">{t('insurance.verification.col.covered')}</th>
                        <th className="px-3 py-2 font-semibold text-gray-700 w-32">{t('insurance.verification.col.limit')}</th>
                        <th className="px-3 py-2 font-semibold text-gray-700 w-32">{t('insurance.verification.col.deductible')}</th>
                        <th className="px-3 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {declaredRows.map((row, idx) => {
                        const expected = expectedData?.expected?.find(g => g.code === row.code);
                        const isExtra = !expected;
                        return (
                          <tr key={row._key} className={`border-t border-gray-100 ${isExtra ? 'bg-blue-50/30' : ''}`}>
                            <td className="px-3 py-2 text-center text-gray-400 text-xs">{idx + 1}</td>
                            <td className="px-3 py-2">
                              {isExtra ? (
                                <input
                                  type="text"
                                  value={row.label}
                                  onChange={(e) => {
                                    updateRow(row._key, 'label', e.target.value);
                                    if (!row.code) updateRow(row._key, 'code', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '_'));
                                  }}
                                  placeholder={t('insurance.verification.guaranteeNamePlaceholder')}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                              ) : (
                                <span className="font-medium text-gray-900">{guaranteeLabel(t, row.code, row.label)}</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center text-xs text-gray-500">
                              {expected ? (
                                <div>
                                  <div>{expected.limit ? formatAmount(expected.limit) : t('insurance.verification.unlimited')}</div>
                                  {expected.deductible > 0 && (
                                    <div className="text-gray-400">{t('insurance.verification.deductibleShort', { amount: formatAmount(expected.deductible) })}</div>
                                  )}
                                </div>
                              ) : (
                                                <span className="text-gray-300 italic">{t('insurance.verification.outOfModel')}</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <input
                                type="checkbox"
                                checked={!!row.included}
                                onChange={(e) => updateRow(row._key, 'included', e.target.checked)}
                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                value={row.limit || ''}
                                onChange={(e) => updateRow(row._key, 'limit', e.target.value)}
                                disabled={!row.included}
                                placeholder={t('insurance.verification.unlimited')}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm disabled:opacity-40 disabled:bg-gray-100"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                value={row.deductible || ''}
                                onChange={(e) => updateRow(row._key, 'deductible', e.target.value)}
                                disabled={!row.included}
                                placeholder="0"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm disabled:opacity-40 disabled:bg-gray-100"
                              />
                            </td>
                            <td className="px-3 py-2">
                              {isExtra && (
                                <button onClick={() => removeRow(row._key)} className="p-1 text-gray-400 hover:text-red-500">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <button
                  onClick={addRow}
                  className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  {t('insurance.verification.addGuarantee')}
                </button>
              </div>
            ) : (
              /* ── PHASE B: Comparison ── */
              <div className="flex-1 overflow-y-auto px-6 py-4">
                {/* Summary bar */}
                {comparison && (
                  <>
                    <div className="flex items-center gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-1.5">
                        <ShieldCheck className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium">{t('insurance.verification.compliantCount', { n: comparison.summary.ok_count })}</span>
                      </div>
                      {comparison.summary.missing_count > 0 && (
                        <div className="flex items-center gap-1.5">
                          <AlertTriangle className="w-4 h-4 text-red-600" />
                          <span className="text-sm font-medium text-red-700">{t('insurance.verification.missingCount', { n: comparison.summary.missing_count })}</span>
                        </div>
                      )}
                      {comparison.summary.discrepancy_count > comparison.summary.missing_count && (
                        <div className="flex items-center gap-1.5">
                          <ShieldAlert className="w-4 h-4 text-amber-600" />
                          <span className="text-sm font-medium text-amber-700">{t('insurance.verification.discrepancyCount', { n: comparison.summary.discrepancy_count - comparison.summary.missing_count })}</span>
                        </div>
                      )}
                      {comparison.summary.extra_count > 0 && (
                        <div className="flex items-center gap-1.5">
                          <Info className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium text-blue-700">{t('insurance.verification.extraCount', { n: comparison.summary.extra_count })}</span>
                        </div>
                      )}
                      <div className="ml-auto">
                        <button
                          onClick={switchToEntry}
                          className="text-xs text-gray-500 hover:text-blue-600 underline"
                        >
                          {t('insurance.verification.editInsurerData')}
                        </button>
                      </div>
                    </div>

                    {error && (
                      <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                        {error}
                      </div>
                    )}

                    {/* Comparison table */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 text-left">
                            <th className="px-4 py-2.5 font-semibold text-gray-700">{t('insurance.verification.col.guarantee')}</th>
                            <th className="px-4 py-2.5 font-semibold text-gray-700">{t('insurance.verification.col.expected')}</th>
                            <th className="px-4 py-2.5 font-semibold text-gray-700">{t('insurance.verification.col.actual')}</th>
                            <th className="px-4 py-2.5 font-semibold text-gray-700">{t('insurance.verification.col.discrepancy')}</th>
                            <th className="px-4 py-2.5 font-semibold text-gray-700 text-center w-24">{t('insurance.verification.col.compliant')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {comparison.comparison.map((row) => {
                            const style = SEVERITY_STYLES[row.severity] || SEVERITY_STYLES.ok;
                            const IconComp = style.icon;
                            const isAccepted = overrides[row.code] || false;

                            return (
                              <tr key={row.code} className={`border-t border-gray-100 ${style.bg}`}>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <IconComp className={`w-4 h-4 ${style.text} flex-shrink-0`} />
                                    <span className="font-medium text-gray-900">{guaranteeLabel(t, row.code, row.label)}</span>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-gray-600">
                                  {row.expected ? (
                                    <div>
                                      <span>{t('insurance.verification.included')}</span>
                                      {row.expected.limit && <span className="text-xs ml-1">/ {formatAmount(row.expected.limit)}</span>}
                                      {row.expected.deductible > 0 && <span className="text-xs ml-1 text-gray-400">{t('insurance.verification.deductibleParen', { amount: formatAmount(row.expected.deductible) })}</span>}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400 italic">{t('insurance.verification.notRequired')}</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-gray-600">
                                  {row.actual ? (
                                    row.actual.included === false ? (
                                      <span className="font-bold text-red-600">{t('insurance.verification.notCovered')}</span>
                                    ) : (
                                      <div>
                                        <span>{t('insurance.verification.included')}</span>
                                        {row.actual.limit && <span className="text-xs ml-1">/ {formatAmount(row.actual.limit)}</span>}
                                        {row.actual.deductible > 0 && <span className="text-xs ml-1 text-gray-400">{t('insurance.verification.deductibleParen', { amount: formatAmount(row.actual.deductible) })}</span>}
                                      </div>
                                    )
                                  ) : (
                                    <span className="font-bold text-red-600">{t('insurance.verification.absent')}</span>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  {row.status === 'ok' ? (
                                    <span className="text-green-600">-</span>
                                  ) : row.status === 'missing' ? (
                                    <span className="text-red-600 font-medium">{t('insurance.verification.missingGuarantee')}</span>
                                  ) : row.status === 'extra' ? (
                                    <span className="text-blue-600 text-xs">{t('insurance.verification.outOfModelNotRequired')}</span>
                                  ) : (
                                    <div>
                                      {Array.isArray(row.discrepancy) ? row.discrepancy.map((d, i) => (
                                        <div key={i} className={`text-xs ${style.text}`}>
                                          {d.type === 'lower_limit' && t('insurance.verification.lowerLimit', { actual: formatAmount(d.actual_value), expected: formatAmount(d.expected_value) })}
                                          {d.type === 'higher_deductible' && t('insurance.verification.higherDeductible', { actual: formatAmount(d.actual_value), expected: formatAmount(d.expected_value) })}
                                        </div>
                                      )) : (
                                        <span className={`text-xs ${style.text}`}>{t(`insurance.verification.severity.${style.severity}`)}</span>
                                      )}
                                    </div>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {row.status === 'ok' ? (
                                    <Check className="w-5 h-5 text-green-600 mx-auto" />
                                  ) : row.status === 'extra' ? (
                                    <Info className="w-4 h-4 text-blue-400 mx-auto" />
                                  ) : (
                                    <label className="flex items-center justify-center cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={isAccepted}
                                        onChange={() => toggleOverride(row.code)}
                                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                      />
                                    </label>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Global notes */}
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('insurance.verification.notes')}</label>
                      <textarea
                        value={globalNotes}
                        onChange={(e) => setGlobalNotes(e.target.value)}
                        placeholder={t('insurance.verification.notesPlaceholder')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                        rows={2}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {phase === 'entry' && (
              <span>{t('insurance.verification.toDeclare', { n: declaredRows.filter(r => r.included).length })}</span>
            )}
            {phase === 'comparison' && comparison && (
              <>
                <span className="font-medium">{conformCount}/{totalCount}</span> {t('insurance.verification.compliantGuarantees')}
                {unacceptedDiscrepancies > 0 && (
                  <span className="text-amber-600 ml-2">{t('insurance.verification.unacceptedDiscrepancies', { n: unacceptedDiscrepancies })}</span>
                )}
              </>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('common.actions.cancel')}
            </button>

            {phase === 'entry' && (
              <button
                onClick={handleSaveDeclared}
                disabled={savingDeclared || declaredRows.length === 0}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {savingDeclared ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {t('insurance.verification.saveAndVerify')}
              </button>
            )}

            {phase === 'comparison' && comparison && (
              <button
                onClick={handleValidate}
                disabled={saving}
                className="px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                {t('insurance.verification.validate')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default InsuranceVerificationModal;
